import { useState, useEffect } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import Spinner from "../Components/Spinner";
import ErrorMessage from "../Components/ErrorMessage";
import { PiLeaf } from "react-icons/pi";
import MandatorySign from "../Components/MandatorySign";
import { MdOutlineCheckCircle, MdOutlineCircle } from "react-icons/md";
import NewOrganizationModal from "../Organizations/NewOrganizationModal";
import { CiCircleAlert, CiCircleInfo } from "react-icons/ci";
import { useNavigate, useParams } from "react-router-dom";
import { memoryOptions, cpuOptions, regionOptions } from "../MCPs/ContstantValues"
import { useLanguage } from "../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const PublishMcpModal = ({
    setPopupMex,
    account_id,
    show,
    handleClose,
    runtime,
    mcpData,
    fetchMcpData,
    streamMcpStatus,
    singleOrgData
}) => {
    const {t} = useLanguage()
    const navigate = useNavigate()
    const { org_id, mcp_id, version_n } = useParams()
    const [loadingSave, setLoadingSave] = useState(false);
    const [errorMessage, setErrorMessage] = useState({ message: "", display: false });
    const [openComputeSettings, setOpenComputeSettings] = useState(false)
    const [openOrgModal, setOpenOrgModal] = useState(false)
    const [formData, setFormData] = useState({
        organization_ids: [],
        memory: memoryOptions[0],
        cpu: 1,
        region: regionOptions.find(r => r.value === "europe-west8"),
        max_instances: 1,
        network_mode: "restricted",
        allowed_ips: "",
    });

    useEffect(() => {
        if (mcpData && show) {
            setFormData({
                organization_ids: mcpData.organization_ids || [],
                memory: memoryOptions.find(m => m.value === mcpData.memory) || memoryOptions[0],
                cpu: mcpData.cpu || 1,
                region: regionOptions.find(r => r.value === mcpData.region) || regionOptions.find(r => r.value === "europe-west8"),
                max_instances: mcpData.max_instances || 1,
                network_mode: mcpData.network_mode || "restricted",
                allowed_ips: mcpData.allowed_ips || "",
            });
        }
    }, [mcpData, show, mcp_id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        setErrorMessage({ message: "", display: false });

        if (formData.organization_ids.length == 0) {
            setErrorMessage({ message: t.storage.org_required, display: true });
            return;
        }

        if (formData.max_instances > 4) {
            setErrorMessage({ message: t.abilities.max_four_inst, display: true });
            return;
        }

        setLoadingSave(true);
        try {
            let ips = ""
            formData.network_mode == "restricted" ? ips = formData.allowed_ips : ips = "0.0.0.0/0"

            const response = await fetch(`${GEN_BACKEND_LINK}/publish_mcp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id,
                    org_id,
                    mcp_id,
                    organization_ids: formData.organization_ids,
                    memory: formData.memory.value,
                    cpu: formData.cpu,
                    region: formData.region.value,
                    max_instances: formData.max_instances,
                    network_mode: formData.network_mode,
                    allowed_ips: ips,
                    version: version_n,
                    runtime: runtime
                }),
            });

            const result = await response.json();

            if (result.error == "not_authorized") {
                CloseModal()
                setPopupMex({ active: true, type: "error", message: "Organization auth..." })
                setTimeout(() => {
                    setPopupMex({ active: false, type: null, message: "" })
                    navigate(`/e8console/organizations`)
                }, 5000);
            }

            if (!response.ok) throw new Error("Failed to deploy MCP");

            if (result.status == "success") {
                const mainUrl = `/e8console/organizations/${org_id}/abilities`;
                const version_to_navigate = Number(version_n)

                const targetPath =
                    runtime === "python"
                        ? `${mainUrl}/ability-builder/python/${result.mcp_id}/${version_to_navigate}`
                        : `${mainUrl}/ability-builder/javascript/${result.mcp_id}/${version_to_navigate}`;

                navigate(targetPath);
                CloseModal()
            }
        } catch (err) {
            setErrorMessage({ message: err.message, display: true });
        } finally {
            setLoadingSave(false);
            setErrorMessage({ message: "", display: false });
            fetchMcpData()
            streamMcpStatus(mcp_id)
        }
    };

    const CloseModal = () => {
        setFormData({
            organization_ids: [],
            memory: memoryOptions[0],
            cpu: 1,
            region: regionOptions.find(r => r.value === "europe-west8"),
            max_instances: 1,
            network_mode: "restricted",
            allowed_ips: "",
        })
        setErrorMessage({ message: "", display: false })
        setLoadingSave(false)
        handleClose()
    }

    const handleSetTest = () => {
        setFormData(prev => ({
            ...prev,
            organization_ids: [`test-${org_id}`]
        }));
    };

    const handleOrgSelect = (orgId) => {
        setFormData(prev => {
            const alreadySelected = prev.organization_ids.includes(orgId);

            const newIds = prev.organization_ids.filter(id => id !== `test-${org_id}`);

            return {
                ...prev,
                organization_ids: alreadySelected
                    ? newIds.filter(id => id !== orgId)
                    : [...newIds, orgId]
            };
        });
    };

    const toggleSettings = () => {
        openComputeSettings ? setOpenComputeSettings(false) : setOpenComputeSettings(true)
    }

    return (
        <Modal show={show} onHide={CloseModal} centered size="md" className="mcp_modal_custom">
            <div className="modal_header">
                <p className="modal_header_title">
                    {t.abilities.publish_ability}
                    <span>{version_n && `v${version_n}`}</span>
                </p>
            </div>

            <div className="modal_main_cont">
                <div className="modal_profile_input_cont">
                    <div className="label_input">
                        <label className="input_label">{t.sidebar.organizations} <MandatorySign message={t.mandatory_field} /></label>
                        <Dropdown>
                            <Dropdown.Toggle
                                as="div"
                                className="register_form_first fade-in"
                            >
                                {formData.organization_ids.length > 0
                                    ? formData.organization_ids.map(id => {
                                        if (id === `test-${org_id}`) return "Test mode";
                                        if (id === "public") return "Public";
                                        if (singleOrgData && singleOrgData.id === id) return singleOrgData.name;
                                        return id; // fallback
                                    }).join(", ")
                                    : t.select
                                }
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="custom_dropdown_menu">
                                <div className="dropdown_container fade-in">
                                    <button
                                        className="settings_link"
                                        onClick={handleSetTest}
                                    >
                                        <i>
                                            {formData.organization_ids.includes(`test-${org_id}`)
                                                ? <MdOutlineCheckCircle />
                                                : <MdOutlineCircle />}
                                        </i>
                                        Test mode
                                    </button>
                                    {/* <button
                                        className="settings_link"
                                        onClick={handleSetPublic}
                                    >
                                        <i>
                                            {formData.organization_ids.includes("public")
                                                ? <MdOutlineCheckCircle />
                                                : <MdOutlineCircle />}
                                        </i>
                                        Public to anyone
                                    </button> */}
                                    {singleOrgData && (
                                        <button
                                            key={singleOrgData.id}
                                            onClick={() => handleOrgSelect(singleOrgData.id)}
                                            className="settings_link"
                                        >
                                            <i>
                                                {formData.organization_ids.includes(singleOrgData.id)
                                                    ? <MdOutlineCheckCircle />
                                                    : <MdOutlineCircle />}
                                            </i>
                                            {singleOrgData.name}
                                        </button>
                                    )}
                                    {/* <button
                                        onClick={() => setOpenOrgModal(true)}
                                        className="settings_link"
                                    >
                                        <i><BiPlus /></i> Create new organization
                                    </button> */}
                                </div>
                            </Dropdown.Menu>

                        </Dropdown>
                        <div className="selected_items_container">
                            {formData.organization_ids.map(id => {
                                if (id === "public") {
                                    return (
                                        <p className="role_warning_text" key={id}>
                                            <i><CiCircleAlert /></i>{" "}
                                           {t.abilities.public_warning}
                                        </p>
                                    );
                                }

                                if (id === `test-${org_id}`) {
                                    return (
                                        <p className="role_warning_text" key={id}>
                                            <i><CiCircleAlert /></i>{" "}
                                            {t.abilities.private_warning}
                                        </p>
                                    );
                                }

                                // if (singleOrgData && singleOrgData.id === id) {
                                //     return (
                                //         <span key={id} className="selected_item_chip">
                                //             {singleOrgData.name}
                                //             <i className="remove_item_icon" onClick={() => removeOrganization(id)}>
                                //                 <FaTimes />
                                //             </i>
                                //         </span>
                                //     );
                                // }

                                return null;
                            })}
                        </div>
                    </div>

                    {formData.organization_ids.length > 0 && (
                        <div>

                            <div className={`toggle_input_cont ${openComputeSettings ? "toggle_input_cont_open" : ""}`}>
                                <div className="toggle_input_cont_header" onClick={toggleSettings}>
                                    <p>{t.abilities.compute_settings}</p>
                                    <i className={`fa-solid fa-chevron-${openComputeSettings ? "up" : "down"}`}></i>
                                </div>
                                <div className={`toggle_input_cont_inner ${openComputeSettings ? "toggle_input_cont_inner_open" : ""}`}>
                                    <div className="persona_reg">
                                        <div className="label_input">
                                            <label className="input_label">{t.abilities.memory} <MandatorySign message={t.mandatory_field} /></label>
                                            <Dropdown drop="up">
                                                <Dropdown.Toggle as="div" className="register_form_first fade-in pointer">
                                                    {formData.memory.label}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className="custom_dropdown_menu">
                                                    {memoryOptions.map((mem) => (
                                                        <Dropdown.Item key={mem.value} onClick={() => setFormData({ ...formData, memory: mem })} className={`settings_link ${mem.value == formData.memory.value ? "settings_link_selected" : ""}`}>
                                                            {mem.label}
                                                        </Dropdown.Item>
                                                    ))}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>

                                        <div className="label_input">
                                            <label className="input_label">vCPU <MandatorySign message={t.mandatory_field} /></label>
                                            <Dropdown drop="up">
                                                <Dropdown.Toggle as="div" className="register_form_first fade-in pointer">
                                                    {formData.cpu} vCPU
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className="custom_dropdown_menu">
                                                    {cpuOptions.map((cpu) => {
                                                        const isDisable = cpu > formData.memory.maxCpu;
                                                        return (
                                                            <Dropdown.Item
                                                                key={cpu}
                                                                disabled={isDisable}
                                                                onClick={() => !isDisable && setFormData({ ...formData, cpu })}
                                                                className={`settings_link ${isDisable ? 'text-muted disabled_cpu' : ''} ${cpu == formData.cpu ? "settings_link_selected" : ""}`}
                                                            >
                                                                {cpu} vCPU {isDisable && "(Requires more RAM)"}
                                                            </Dropdown.Item>
                                                        );
                                                    })}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>
                                    </div>
                                    <div className="persona_reg">
                                        <div className="label_input">
                                            <label className="input_label">{t.abilities.scaling} <MandatorySign message={t.mandatory_field} /></label>
                                            <input
                                                type="number"
                                                name="max_instances"
                                                value={formData.max_instances}
                                                onChange={handleChange}
                                                min={1}
                                                max={5}
                                                className="register_form_first fade-in"
                                            />
                                        </div>
                                         <div className="persona_reg">
                                        <div className="label_input">
                                            <label className="input_label">{t.abilities.region} <MandatorySign message={t.abilities.region_warning} icon="info" /></label>
                                            <p className="register_form_first register_form_first_disabled fade-in">
                                                {formData.region.label}
                                            </p>
                                        </div>
                                    </div>
                                        {/*<div className="label_input">
                                            <label className="input_label">Network Access <MandatorySign message={"Network access is required"} /></label>
                                            <Dropdown drop="up">
                                                <Dropdown.Toggle as="div" className="register_form_first fade-in pointer">
                                                    {formData.network_mode === "public" ? "Public" : "Restricted"}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className="custom_dropdown_menu">
                                                    <Dropdown.Item
                                                        onClick={() => setFormData({ ...formData, network_mode: "public" })}
                                                        className={`settings_link ${formData.network_mode == "public" ? "settings_link_selected" : ""}`}
                                                    >
                                                        Public
                                                    </Dropdown.Item>
                                                    <Dropdown.Item
                                                        onClick={() => setFormData({ ...formData, network_mode: "restricted", allowed_ips: "" })}
                                                        className={`settings_link ${formData.network_mode == "restricted" ? "settings_link_selected" : ""}`}
                                                    >
                                                        Restricted
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div> */}

                                        {/* {formData.network_mode === "restricted" && (
                                            <div className="label_input">
                                                <label className="input_label">
                                                    Allowed IPs (CIDR)
                                                    <MandatorySign message={"At least one IP range required"} />
                                                </label>
                                                <input
                                                    type="text"
                                                    name="allowed_ips"
                                                    value={formData.allowed_ips}
                                                    onChange={handleChange}
                                                    placeholder="Ex. 192.168.1.0/24, 10.0.0.1"
                                                    className="register_form_first fade-in"
                                                />
                                            </div>
                                        )} */}
                                    </div>
                                   
                                </div>
                            </div>
                            {formData.network_mode === "public" && (
                                <div className="role_warning_text_cont fade-in">
                                    <p className="role_warning_text">
                                        <strong>Warning <i><CiCircleAlert /></i></strong><br />
                                        {t.abilities.network_warning}
                                    </p>
                                </div>
                            )}
                            {version_n && (
                                <div className="role_info_text_cont fade-in">
                                    <p className="role_warning_text">
                                        {t.abilities.publishing} v{version_n}.<br/> 
                                        {t.abilities.publishing_warn}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="modal_footer">
                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>
                <div className="modal_footer_btn_cont">
                    <button className="modale_save_btn" onClick={handleSave} disabled={loadingSave}>
                        {loadingSave ? <Spinner loading={true} /> : t.abilities.publish}
                    </button>
                    <button className="modale_close_btn" onClick={CloseModal}>
                        {t.close}
                    </button>
                </div>
            </div>


            <NewOrganizationModal
                account_id={account_id}
                show={openOrgModal}
                handleClose={() => setOpenOrgModal(false)}
            />
        </Modal>
    );
};

export default PublishMcpModal;